import { HREF } from '../../../../core/src/lib/constants/href-merch-store.constant';
import { CONTACT } from '../../../../core/src/lib/constants/contact.constant';

export enum OrderItemTrackingStatusEnum {
  Ordered = 'Ordered',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  Canceled = 'Canceled',
  Submitted = 'Submitted',

  // admin only
  PaymentPending = 'Pending',
  PaymentFailed = 'PaymentFailed',
  FraudHeld = 'FraudHeld',
  Failed = 'Failed',
  ReturnReceived = 'ReturnReceived',
  ReturnStarted = 'ReturnStarted',
  Declined = 'Declined',

  // For pseudo items only on FE.
  Returned = 'Returned',
  DeliveredInReturn = 'DeliveredInReturn',
}
export enum OrderStatusEnum {
  Ordered = 'Ordered',
  Canceled = 'Canceled',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  Returned = 'Returned',
  Failed = 'Failed',
  FraudHeld = 'FraudHeld',
  Declined = 'Declined',
}

export interface OrderHistoryTableItem {
  id: string;
  number: string;
  createdAt: string;
  status: OrderItemTrackingStatusEnum;
  amountUsd: number;
  amountPoints: number;
}

export interface OrderDetailsDTO {
  id: string;
  dateOrdered: string;
  number: string;
  shipping: ShippingDetails;
  billingAddress: BillingDetails;
  paymentDetails: PaymentDetails;
  lineItems: OrderLineItemDTO[];
  total: TotalDetails;
  status: OrderStatusEnum;
}

export interface OrderDetails extends OrderDetailsDTO {
  lineItems: OrderLineItem[];
}

export interface BillingDetails {
  city: string;
  countryCode: string;
  postalCode: string;
  addressLine1: string;
  addressLine2?: string | null;
  addressLine3?: string | null;
  stateCode: string;
}

export interface ShippingDetails {
  firstName: string;
  lastName: string;
  telephoneNumber: string;
  emailAddress: string;
  city: string;
  postalCode: string;
  addressLine1: string;
  addressLine2?: string | null;
  addressLine3?: string | null;
  state: string;
  countryCode: string;
}

export interface PaymentDetails {
  pointsRedeemed: number;
  billedToCard: number;
  card: CardDetails;
}

export interface CardDetails {
  cardType: string;
  lastFourDigits: string;
  //TODO: maybe there also would be card provider logo url, will see during narvar intgration
}

export interface TotalDetails {
  subtotalPoints: number;
  subtotalPrice: number;
  totalPoints: number;
  totalPrice: number;
  tax: number;
  taxPoints: number;
}

export type OrderLineItemDTO = {
  id: string;
  productId: number;
  dateOrdered: string;
  name: string;
  brandName: string;
  price: number;
  imageUrl: string;
  quantity: number;
  estimatedShippingDays: string;
  status: OrderItemTrackingStatusEnum;
  tracking: OrderItemTracking;
  total: OrderItemTotal;
  refund: OrderItemRefund;
  refundCard: CardDetails;
  pointsRefunded?: number;
  usdRefunded?: number;
  amountUsd?: number;
  amountPoints?: number;
  relatedLineItemId?: string | null;
  pointConversionRate?: number;
};

export type OrderLineItem = OrderLineItemDTO & Required<{ lineItemIndex: number }>;

export type OrderItemTracking = {
  trackingNumber: string | null;
  expectedDeliveryAt: string | null;
  deliveredAt: string | null;
  canceledAt: string | null;
  shippingAvailableInDays: string | null;
  deliveryServiceLink: string | null;
};

export type OrderItemTotal = {
  subtotalPoints: number;
  subtotalPrice: number;
  totalPoints: number;
  totalPrice: number;
  tax: number;
  taxPoints: number;
};

export type OrderItemRefund = {
  totalAmountRefunded: number;
  pointsCredited: number;
};

export type OrderLineItemsByStatus = Record<OrderItemTrackingStatusEnum, OrderLineItem[]>;

export type OrderDetailsWithGroupedLineItems = OrderDetails & {
  groupedLineItems: OrderLineItemsByStatus;
};

export const ORDER_ITEM_STATUS_TO_LABEL: Record<OrderItemTrackingStatusEnum, string> = {
  [OrderItemTrackingStatusEnum.Ordered]: 'Ordered',
  [OrderItemTrackingStatusEnum.PaymentPending]: 'Ordered',
  [OrderItemTrackingStatusEnum.PaymentFailed]: '',
  [OrderItemTrackingStatusEnum.FraudHeld]: '',
  [OrderItemTrackingStatusEnum.Shipped]: 'Shipped',
  [OrderItemTrackingStatusEnum.Delivered]: 'Delivered',
  [OrderItemTrackingStatusEnum.Canceled]: 'Order canceled',
  [OrderItemTrackingStatusEnum.Returned]: 'Returns from this order',
  [OrderItemTrackingStatusEnum.ReturnReceived]: 'Returns from this order',
  [OrderItemTrackingStatusEnum.ReturnStarted]: 'Returns from this order',
  [OrderItemTrackingStatusEnum.DeliveredInReturn]: 'Delivered',
  [OrderItemTrackingStatusEnum.Submitted]: 'Submitted',
  [OrderItemTrackingStatusEnum.Failed]: 'Failed',
  [OrderItemTrackingStatusEnum.Declined]: 'Declined',
};

export const ORDER_ITEM_DELIVERY_STATUSES: OrderItemTrackingStatusEnum[] = [
  OrderItemTrackingStatusEnum.Ordered,
  OrderItemTrackingStatusEnum.Shipped,
  OrderItemTrackingStatusEnum.Delivered,
];

export const isReturnedOrderItem = (status: OrderItemTrackingStatusEnum): boolean =>
  status === OrderItemTrackingStatusEnum.Returned;

export const isDeliveredItemInReturn = <T extends { status: OrderItemTrackingStatusEnum }>(item: T): boolean =>
  item.status === OrderItemTrackingStatusEnum.DeliveredInReturn;

export const PSEUDO_ITEM_STATUSES: Partial<Record<OrderItemTrackingStatusEnum, OrderItemTrackingStatusEnum>> = {
  [OrderItemTrackingStatusEnum.Returned]: OrderItemTrackingStatusEnum.DeliveredInReturn,
};

export const isOneOfDeliveryStatuses = (status: any): boolean =>
  [
    OrderItemTrackingStatusEnum.Ordered,
    OrderItemTrackingStatusEnum.Canceled,
    OrderItemTrackingStatusEnum.Shipped,
    OrderItemTrackingStatusEnum.Delivered,
  ].includes(status);

export const mapDtoToInternalOrder = (order: OrderDetailsDTO): OrderDetails => {
  const products: OrderLineItem[] = order.lineItems.map((product, index) => ({ ...product, lineItemIndex: index }));

  return {
    ...order,
    lineItems: products,
  };
};

export const mapOrderItemStatusToDeliveryPointOfViewStatus = (
  status: OrderItemTrackingStatusEnum,
): OrderItemTrackingStatusEnum => {
  switch (status) {
    case OrderItemTrackingStatusEnum.Returned:
      return OrderItemTrackingStatusEnum.Delivered;

    case OrderItemTrackingStatusEnum.Canceled:
      return OrderItemTrackingStatusEnum.Ordered;

    default:
      return status;
  }
};

export const pickDateRelatedToStatus = (orderItem: OrderLineItem): string | undefined => {
  const { status } = orderItem;
  if (status === OrderItemTrackingStatusEnum.Ordered) {
    return orderItem.dateOrdered;
  }

  if (status === OrderItemTrackingStatusEnum.Shipped) {
    return orderItem.tracking.expectedDeliveryAt ?? undefined;
  }

  if (status === OrderItemTrackingStatusEnum.Delivered) {
    return orderItem.tracking.deliveredAt ?? undefined;
  }

  if (status === OrderItemTrackingStatusEnum.Canceled) {
    return orderItem.tracking.canceledAt ?? undefined;
  }

  if (status === OrderItemTrackingStatusEnum.Returned) {
    return undefined;
  }

  return undefined;
};

export const getRelatedToStatusDescription = (status: OrderItemTrackingStatusEnum): string => {
  if (status === OrderItemTrackingStatusEnum.Ordered) {
    return 'We’re working hard to get your order shipped. Please check back later for your tracking number. ';
  }

  if (status === OrderItemTrackingStatusEnum.Shipped) {
    return '';
  }

  if (status === OrderItemTrackingStatusEnum.Delivered) {
    return `If there is an issue with your order, you can return eligible items up to 30 days from your delivery date. Call us at <a class="link-phone" href="tel:${CONTACT.REQUEST_RETURN_PHONE}">${CONTACT.REQUEST_RETURN_PHONE}</a> to request a return or `;
  }

  if (status === OrderItemTrackingStatusEnum.Canceled) {
    return 'It may take up to 4 business days to process your refund.';
  }

  if (status === OrderItemTrackingStatusEnum.Returned) {
    return 'Your return is in progress. It may take up to 21 days to process your refund once we receive it. Your refund details show the total amount refunded for your return. If returning more than one Item, you’ll be refunded for each item in your Chase account separately. ';
  }

  return '';
};

export const isShowProgressBarForStatus = (status: OrderItemTrackingStatusEnum): boolean => {
  return [OrderItemTrackingStatusEnum.Shipped, OrderItemTrackingStatusEnum.Delivered].includes(status);
};

export const LINK_LABEL_BY_STATUS: Record<OrderItemTrackingStatusEnum, string> = {
  [OrderItemTrackingStatusEnum.Canceled]: '',
  [OrderItemTrackingStatusEnum.Delivered]: 'learn more about returns and refunds.',
  [OrderItemTrackingStatusEnum.DeliveredInReturn]: '',
  [OrderItemTrackingStatusEnum.Ordered]: 'Learn more about shipping and delivery.',
  [OrderItemTrackingStatusEnum.Returned]: 'Learn more about returns and refunds.',
  [OrderItemTrackingStatusEnum.Shipped]: '',
  [OrderItemTrackingStatusEnum.Submitted]: '',
  [OrderItemTrackingStatusEnum.PaymentPending]: '',
  [OrderItemTrackingStatusEnum.PaymentFailed]: '',
  [OrderItemTrackingStatusEnum.FraudHeld]: '',
  [OrderItemTrackingStatusEnum.ReturnReceived]: '',
  [OrderItemTrackingStatusEnum.ReturnStarted]: '',
  [OrderItemTrackingStatusEnum.Failed]: '',
  [OrderItemTrackingStatusEnum.Declined]: '',
};
export const LINK_HREF_BY_STATUS: Record<OrderItemTrackingStatusEnum, string> = {
  [OrderItemTrackingStatusEnum.Canceled]: '',
  [OrderItemTrackingStatusEnum.Delivered]: `${HREF.INFO}/${HREF.RETURNS_AND_REFUNDS}`,
  [OrderItemTrackingStatusEnum.DeliveredInReturn]: '',
  [OrderItemTrackingStatusEnum.Ordered]: `${HREF.INFO}/${HREF.SHIPPING_AND_DELIVERY}`,
  [OrderItemTrackingStatusEnum.Returned]: `${HREF.INFO}/${HREF.RETURNS_AND_REFUNDS}`,
  [OrderItemTrackingStatusEnum.Shipped]: '',
  [OrderItemTrackingStatusEnum.Submitted]: '',
  [OrderItemTrackingStatusEnum.PaymentPending]: '',
  [OrderItemTrackingStatusEnum.PaymentFailed]: '',
  [OrderItemTrackingStatusEnum.FraudHeld]: '',
  [OrderItemTrackingStatusEnum.ReturnReceived]: '',
  [OrderItemTrackingStatusEnum.ReturnStarted]: '',
  [OrderItemTrackingStatusEnum.Failed]: '',
  [OrderItemTrackingStatusEnum.Declined]: '',
};
export const linkLabelByStatus = (status: OrderItemTrackingStatusEnum): string => LINK_LABEL_BY_STATUS[status];
export const linkHrefByStatus = (status: OrderItemTrackingStatusEnum): string => LINK_HREF_BY_STATUS[status];

export const groupOrderItemsByStatus = (order: OrderDetails): OrderDetailsWithGroupedLineItems => ({
  ...order,
  groupedLineItems: order.lineItems.reduce(
    (prev, cur) => ({
      ...prev,
      [cur.status]: [...prev[cur.status], { ...cur, lineItemIndex: prev[cur.status].length }],
    }),
    {
      [OrderItemTrackingStatusEnum.Ordered]: [],
      [OrderItemTrackingStatusEnum.Submitted]: [],
      [OrderItemTrackingStatusEnum.Shipped]: [],
      [OrderItemTrackingStatusEnum.Canceled]: [],
      [OrderItemTrackingStatusEnum.Delivered]: [],
      [OrderItemTrackingStatusEnum.DeliveredInReturn]: [],
      [OrderItemTrackingStatusEnum.Returned]: [],
      [OrderItemTrackingStatusEnum.PaymentPending]: [],
      [OrderItemTrackingStatusEnum.PaymentFailed]: [],
      [OrderItemTrackingStatusEnum.FraudHeld]: [],
      [OrderItemTrackingStatusEnum.ReturnReceived]: [],
      [OrderItemTrackingStatusEnum.ReturnStarted]: [],
      [OrderItemTrackingStatusEnum.Failed]: [],
      [OrderItemTrackingStatusEnum.Declined]: [],
    },
  ),
});
export const STATUS_ORDER: OrderItemTrackingStatusEnum[] = [
  OrderItemTrackingStatusEnum.Ordered,
  OrderItemTrackingStatusEnum.Shipped,
  OrderItemTrackingStatusEnum.Delivered,
  OrderItemTrackingStatusEnum.Canceled,
  OrderItemTrackingStatusEnum.DeliveredInReturn,
  OrderItemTrackingStatusEnum.Returned,
];

export type OrderLineItemAdminPanel = OrderLineItemDTO & {
  dateOrdered: string;
  estimatedShippingDays: string;
  tracking: OrderItemTracking;
  total: OrderItemTotal;
  refund: OrderItemRefund;
  refundCard: CardDetails;
  paymentId: string;
  number: string;
  description: string;
  discountedPrice: number;
  tax: number;
  conversionRate: number;
  trackingNumber: string;
  expectedDeliveryAt: string;
  deliveredAt: string;
  canceledAt: string;
};

export interface OrderDetailsAdminPanel extends OrderDetailsDTO {
  dateOrdered: string;
  number: string;
  shipping: ShippingDetails;
  paymentDetails: PaymentDetails;
  total: TotalDetails;
  price: number;
  createdAt: string;
  amountUsd: number;
  amountPoints: number;
  tax: number;
  shippingAddress: ShippingDetails & {
    stateCode: string;
  };
  lineItems: Array<OrderLineItemAdminPanel>;
}
